import { DateInput } from '@apia/components';
import React from 'react';
import { InputProps, Input as TUIInput } from '@apia/theme';
import { FormContext } from '../form/FormContext';
import { useFieldStatesClassNames } from '../hooks';
import { useField } from '../hooks/useField';
import { useValidationClass } from '../hooks/useValidationClass';
import { validationsStore } from '../validationStore';
import { FieldWrapper } from './FieldWrapper';
import { TDefaultFieldType } from './types';

export type TInput = InputProps & TDefaultFieldType;

export const Input = React.memo(
  React.forwardRef<HTMLInputElement, TInput>(
    (
      {
        avoidSemicolon,
        className,
        label,
        onChange: outerOnChange,
        type,
        /**
         * useField
         */
        avoidRegisterIfExists,
        formName: propsFormName,
        initialValue,
        name,
        removeOnUnmount,
        submitValueParser,
        validationClass,
        validationFunction,
        validationRules,
        value,
        ...props
      },
      ref,
    ) => {
      const { name: contextFormName } = React.useContext(FormContext);
      const formName = propsFormName ?? contextFormName;

      const { actualValidationFunction, actualValidationRules } =
        useValidationClass({
          formName,
          name,
          validationClass,
          validationFunction,
          validationRules,
        });

      const {
        error,
        updateValidations,
        onChange,
        isTouched,
        ...validationProps
      } = useField<string>({
        formName,
        name,
        initialValue:
          initialValue ??
          (value as string) ??
          validationsStore.getFieldStore(formName, name)?.value ??
          '',
        removeOnUnmount,
        submitValueParser,
        validationFunction: actualValidationFunction,
        validationRules: actualValidationRules,
      });

      const handleOnChange = React.useCallback(
        (ev: any) => {
          onChange(ev);
          if (typeof ev === 'string') {
            // Es date
            outerOnChange?.(
              ev as unknown as React.ChangeEvent<HTMLInputElement>,
            );
          } else {
            // Es comun
            outerOnChange?.(ev);
          }
        },
        [onChange, outerOnChange],
      );

      const stateClassName = useFieldStatesClassNames(name);

      return (
        <FieldWrapper
          name={name}
          label={label}
          className={`input ${className ?? ''}`}
          avoidSemicolon={avoidSemicolon}
          title={props.title}
        >
          {type === 'date' ? (
            <DateInput
              {...validationProps}
              aria-label={props['aria-label'] ?? label}
              onError={undefined}
              onChange={handleOnChange}
              value={validationProps.value ?? ''}
              className={stateClassName}
              id={validationProps.name}
              onBlur={(date) => {
                if (props.onBlur) {
                  props.onBlur(
                    date as unknown as React.FocusEvent<
                      HTMLInputElement,
                      Element
                    >,
                  );
                }
              }}
            />
          ) : (
            <TUIInput
              type={type}
              {...props}
              {...validationProps}
              aria-label={props['aria-label'] ?? label}
              ref={ref}
              onChange={handleOnChange}
              value={validationProps.value ?? ''}
              className={stateClassName}
            />
          )}
        </FieldWrapper>
      );
    },
  ),
);

Input.displayName = 'Input';
